import { canRefund } from 'api/orders';
import { getChildrenOfCurrentDecisionNode, getSimplrAccountIdOfSelectedTicket } from 'extractFromState';
import get from 'lodash/get';
import difference from 'lodash/difference';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSelectedOrderId,
  getSelectedOrder,
  getECommerceSearchEmail,
  getSelectedLineItemsIds,
} from 'selectors/tools';
import { handleError } from 'services/ErrorService/ErrorService';
import Question from '../../Common/Question/Question';
import DTQuestionOrInstructionView from '../../DTQuestionOrInstructionView';
import ButtonList from '../../Common/ButtonList/ButtonList';
import { setAdditionalOrderDetails } from 'actions/tools/OrdersSearchActions';
import Loading from '../../../../../LoadingComponent/LoadingComponent';

const DTExpertQuestionNumberOfCyclesNode = () => {
  const dispatch = useDispatch();
  const simplrAccountId = useSelector(getSimplrAccountIdOfSelectedTicket);
  const selectedOrder = useSelector(getSelectedOrder);
  const orderDate = get(selectedOrder, 'createdAt', null);
  const orderId = useSelector(getSelectedOrderId);
  const selectedItemIds = useSelector(getSelectedLineItemsIds);
  const customerEmail = useSelector(getECommerceSearchEmail);
  const decisionNodeChildren = useSelector(getChildrenOfCurrentDecisionNode);
  const accountType = get(selectedOrder, 'extraParams.vendorType', null);

  const [numberOfCycles, setNumberOfCycles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAndStoreRefundableItem = useCallback(async () => {
    const refundPayload = {
      simplrAccountId,
      orderDate,
      orderNumber: String(orderId),
      itemIds: selectedItemIds,
      ...(customerEmail && { customerEmail }),
      ...(accountType && { accountType }),
    };
    const refundData = await canRefund(refundPayload);
    const selectedItemId = selectedItemIds[0];
    const availableRefundCycles = get(refundData[selectedItemId], 'simplrParams.numberOfCycles', null);
    if (!availableRefundCycles?.length) {
      return handleError({
        customMessage: 'Something went wrong with the refundable item.',
        showPopup: true,
      });
    }
    setNumberOfCycles(availableRefundCycles.map(pluralizeMonths));
    setIsLoading(false);
  }, [customerEmail, orderDate, orderId, selectedItemIds, simplrAccountId, accountType]);

  const pluralizeMonths = (refundCycle) => (refundCycle === 1 ? `${refundCycle} month` : `${refundCycle} months`);

  useEffect(() => {
    if (selectedItemIds.length > 1) {
      handleError({
        customMessage: 'Please select only one item from the order when attempting to refund multiple cycles.',
        showPopup: true,
      });
    } else {
      fetchAndStoreRefundableItem().catch((e) => {
        handleError({
          customMessage: 'Something went wrong while checking the refund status of the order.',
          showPopup: true,
        });
      });
    }
  }, [fetchAndStoreRefundableItem, selectedItemIds]);

  // Passed to ButtonList to determine which number of cycles to refund for the selected orderId.
  const selectNumberOfCycleOption = (numberOfCycleChoice) => {
    const trimmedNumberOfCycles = numberOfCycleChoice.replace('months', '').replace('month', '').trim();
    const additionalOrderDetails = {
      [orderId]: {
        numberOfCycles: Number(trimmedNumberOfCycles),
      },
    };
    dispatch(setAdditionalOrderDetails(additionalOrderDetails));
  };

  const getRefundCycleButtonList = () => {
    const nodeRefundCycles = Object.keys(decisionNodeChildren);
    const hiddenRefundCycles = [
      ...difference(numberOfCycles, nodeRefundCycles),
      ...difference(nodeRefundCycles, numberOfCycles),
    ];

    return (
      <ButtonList
        isButtonDisabled={false}
        hiddenButtons={hiddenRefundCycles}
        selectionAction={selectNumberOfCycleOption}
      />
    );
  };

  return isLoading ? (
    <Loading />
  ) : (
    <DTQuestionOrInstructionView titleSection={<Question />} options={getRefundCycleButtonList()} loading={false} />
  );
};

export default DTExpertQuestionNumberOfCyclesNode;
